import React from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from './ChartFrame';
import { PMS_LABELS, fmtMoney, fmtNumber } from '../derive';
import { EmptyState } from '../states';

// Hosts: signup trend for the selected range on the left, top hosts by
// orders on the right. Click a bar or row to open the host drawer.
export default function CanvasHosts({ series = [], total = 0, rows = [], rangeLabel = '', onSelectHost }) {
  const chartData = rows.slice(0, 8).map((r) => ({
    name: (r.name || '?').slice(0, 16),
    orders: r.orderCount,
    _id: r._id,
  }));
  const hasSeries = series.some((row) => (row.count || 0) > 0);

  return (
    <div className="grid h-full min-h-0 gap-3 laptop:grid-cols-[minmax(0,1.1fr)_minmax(320px,1fr)]">
      <ChartFrame
        title="Host signups"
        subtitle={`By day · ${rangeLabel}`}
        right={<span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">{fmtNumber(total)} in range</span>}
      >
        {hasSeries ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="hostGrowthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="count" name="Signups" stroke="#6366f1" strokeWidth={2} fill="url(#hostGrowthFill)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="No host signups" description="Try widening the date range." />
        )}
      </ChartFrame>

      <ChartFrame title="Top hosts by orders" subtitle="In range · click to open">
        {rows.length === 0 ? (
          <EmptyState title="No host orders" description="No orders landed for hosts in this scope." />
        ) : (
          <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#71717a' }} width={96} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                <Bar
                  dataKey="orders"
                  name="Orders"
                  fill="#6366f1"
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                  cursor="pointer"
                  onClick={(b) => {
                    const host = rows.find((r) => String(r._id) === String(b._id));
                    if (host) onSelectHost?.(host);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-hidden rounded-lg border border-zinc-200">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-zinc-50 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-2 py-1">Host</th>
                    <th className="px-2 py-1">PMS</th>
                    <th className="px-2 py-1 text-right">Orders</th>
                    <th className="px-2 py-1 text-right">AOV</th>
                    <th className="px-2 py-1 text-right">Volume</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 bg-white">
                  {rows.slice(0, 4).map((r) => (
                    <tr key={r._id} onClick={() => onSelectHost?.(r)} className="cursor-pointer hover:bg-zinc-50">
                      <td className="max-w-[140px] truncate px-2 py-1 font-semibold text-zinc-900">{r.name}</td>
                      <td className="px-2 py-1 text-zinc-600">{PMS_LABELS[r.pmsType] || '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-zinc-800">{fmtNumber(r.orderCount)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-zinc-800">{fmtMoney(r.aov)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-zinc-800">{fmtMoney(r.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ChartFrame>
    </div>
  );
}
